import { useState, useEffect, useMemo, useCallback, Fragment } from 'react'
import { useSetRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import { endOfDay } from 'date-fns'
import TabNavigation from '@/components/TabNavigation'
import ConnectWalletView from '@/components/ConnectWalletView'
import DatePicker from '@/components/DatePicker'
import Pagination from '@/components/Pagination'
import TableListNoDataRow from '@/components/TableListNoDataRow'
import OrderSideFilter from '@/components/orders/OrderSideFilter'
import MarketFilter from '@/components/orders/MarketFilter'
import OrderHistoryHeaderList from '@/components/orders/OrderHistoryHeaderList'
import LiquidationHistoryHeaderList from '@/components/orders/LiquidationHistoryHeaderList'
import OrderHistory from '@/components/orders/OrderHistory'
import LiquidationHistory from '@/components/orders/LiquidationHistory'
import CancelAllConfirmationPopup from '@/components/CancelAllConfirmationPopup'
import {
  popupNotificationListState,
  latestNotificationValue
} from '@/recoil/Notification'
import { showCancelAllConfirmationPopupState } from '@/recoil/OrderForm'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import fetcher from '@/api/fetcher'
import debounce from '@/utils/debounce'
import './Orders.css'

const MAX_HISTORY_ROW = 10
const ALL_ORDERS = 'ALL_ORDERS'
const OPEN_ORDERS = 'OPEN_ORDERS'
const LIQUIDATIONS = 'LIQUIDATIONS'
const ORDER_TAB_LIST = [
  ALL_ORDERS,
  OPEN_ORDERS,
  'FILL_ORDERS',
  'CANCELLED_ORDERS',
  LIQUIDATIONS
]
const ORDER_TAB_LABEL_MAP = {
  [ALL_ORDERS]: 'All',
  [OPEN_ORDERS]: 'Open',
  FILL_ORDERS: 'Filled',
  CANCELLED_ORDERS: 'Cancelled',
  LIQUIDATIONS: 'Liquidation'
}
const ORDER_STATUS_MAP = {
  [OPEN_ORDERS]: 'INCOMPLETE',
  FILL_ORDERS: 'FILLED',
  CANCELLED_ORDERS: 'CANCELLED'
}

const ORDER_SIDE_FILTER_LIST = ['Buy', 'Sell']

export default function Orders() {
  const { walletReady } = useWalletSelector()

  const setShowCancelAllConfirmationPopup = useSetRecoilState(
    showCancelAllConfirmationPopupState
  )
  const setPopupNotification = useSetRecoilState(popupNotificationListState)
  const latestNotification = useRecoilValue(latestNotificationValue)

  const [isLoading, setIsLoading] = useState(false)
  const [orderTypeTab, setOrderTypeTab] = useState(ALL_ORDERS)
  const [dateRange, setDateRange] = useState([null, null])
  const [marketFilter, setMarketFilter] = useState('')
  const [orderSideFilter, setOrderSideFilter] = useState('')
  const [page, setPage] = useState(0)
  const [totalPage, setTotalPage] = useState(0)
  const [orderDetailMap, setOrderDetailMap] = useState({})
  const [orderDetailActiveMap, setOrderDetailActiveMap] = useState({})
  const [orderHistory, setOrderHistory] = useState({
    type: orderTypeTab,
    list: []
  })
  const [editOrderKey, setEditOrderKey] = useState('')

  const onCancelAll = () => setShowCancelAllConfirmationPopup(true)

  const setFilter = (key, value) => {
    setPage(0)
    if (key === 'type') {
      value === LIQUIDATIONS && setOrderSideFilter('')
      return setOrderTypeTab(value)
    }

    if (key === 'date') {
      return setDateRange(value)
    }

    if (key === 'market') {
      return setMarketFilter(value)
    }

    if (key === 'side') {
      return setOrderSideFilter(value)
    }
  }

  const resetFiler = () => {
    setDateRange([null, null])
    setMarketFilter('')
    setOrderSideFilter('')
    setPage(0)
  }

  const resetEditOrderKey = (isLoading = null) => {
    isLoading !== null && setIsLoading(isLoading)
    setEditOrderKey('')
  }

  const debounceGetOrderHistory = useMemo(
    () =>
      debounce(params => {
        const request =
          orderTypeTab === LIQUIDATIONS ? 'getLiquidatedPositions' : 'getOrders'
        setIsLoading(true)
        fetcher[request](params)
          .then(({ meta, rows }) => {
            setTotalPage(Math.ceil(meta.total / MAX_HISTORY_ROW))
            setOrderHistory({
              type: orderTypeTab,
              list: rows
            })
            setOrderDetailMap({})
            setOrderDetailActiveMap({})
          })
          .catch(() => {
            setPage(0)
            setOrderHistory({ type: orderTypeTab, list: [] })
          })
          .finally(() => setIsLoading(false))
      }, 100),
    [orderTypeTab]
  )

  const getOrderHistory = useCallback(debounceGetOrderHistory, [
    debounceGetOrderHistory
  ])

  const getOrderDetail = useCallback(
    orderId =>
      fetcher.getTradeDetail(orderId).then(({ rows }) => {
        setOrderDetailMap(detailMap => ({
          ...JSON.parse(JSON.stringify(detailMap)),
          [orderId]: rows
        }))
        setOrderDetailActiveMap(orderDetailActiveMap => ({
          ...orderDetailActiveMap,
          [orderId]: true
        }))
      }),
    []
  )

  const cancelOrder = useCallback(
    (symbol, orderId) => {
      setIsLoading(true)
      fetcher.cancelOrder(symbol, orderId).finally(() => setIsLoading(false))
    },
    [setIsLoading]
  )

  useEffect(() => {
    if (!walletReady) {
      return
    }

    const [startDate, endDate] = dateRange
    const params = {
      page: page + 1,
      size: MAX_HISTORY_ROW
    }

    if (![ALL_ORDERS, LIQUIDATIONS].includes(orderTypeTab)) {
      params.status = ORDER_STATUS_MAP[orderTypeTab]
    }

    if (marketFilter !== '') {
      params.symbol = marketFilter
    }

    if (orderSideFilter !== '') {
      params.side = orderSideFilter.toUpperCase()
    }

    if (startDate) {
      params.start_t = startDate.getTime()
    }

    if (endDate) {
      params.end_t = endOfDay(endDate).getTime()
    }

    getOrderHistory(params)
  }, [
    walletReady,
    dateRange,
    orderTypeTab,
    marketFilter,
    orderSideFilter,
    page,
    getOrderHistory,
    setPopupNotification,
    latestNotification
  ])

  const OrderStatusFilterList = ORDER_TAB_LIST.map(key => (
    <div
      key={key}
      className={clsx('order-status-tab', {
        active: orderTypeTab === key
      })}
      onClick={() => orderTypeTab !== key && setFilter('type', key)}
    >
      {ORDER_TAB_LABEL_MAP[key]}
    </div>
  ))

  const isAllOrder = orderHistory.type === ALL_ORDERS
  const isOpenOrder = orderHistory.type === OPEN_ORDERS
  const isLiquidation = orderHistory.type === LIQUIDATIONS
  const HistoryHeaderList = isLiquidation ? (
    <LiquidationHistoryHeaderList />
  ) : (
    <OrderHistoryHeaderList isOpenOrder={isOpenOrder} isAllOrder={isAllOrder} />
  )

  const OrderHistoryList = orderHistory.list.map(history => {
    if (isLiquidation) {
      return (
        <LiquidationHistory key={history.liquidation_id} history={history} />
      )
    }

    const orderId = history.order_id
    const orderDetailList = orderDetailMap[orderId]
    const isOrderDetailActive = orderDetailActiveMap[orderId]
    const toggleOrderDetail = () => {
      isOrderDetailActive &&
        setOrderDetailActiveMap(orderDetailActiveMap => ({
          ...orderDetailActiveMap,
          [orderId]: false
        }))
      if (!isOrderDetailActive) {
        orderDetailList &&
          setOrderDetailActiveMap(orderDetailActiveMap => ({
            ...orderDetailActiveMap,
            [orderId]: true
          }))
        getOrderDetail(orderId)
      }
    }

    return (
      <OrderHistory
        key={orderId}
        isAllOrder={isAllOrder}
        isOpenOrder={isOpenOrder}
        isOrderDetailActive={orderDetailActiveMap[orderId]}
        editOrderKey={editOrderKey}
        history={history}
        orderDetailList={orderDetailList}
        getOrderDetail={getOrderDetail}
        toggleOrderDetail={toggleOrderDetail}
        setEditOrderKey={setEditOrderKey}
        cancelOrder={cancelOrder}
        resetEditOrderKey={resetEditOrderKey}
      />
    )
  })

  const orderHistoryNotEmpty = orderHistory.list.length > 0
  const hasFilter =
    !dateRange.every(v => v === null) ||
    orderSideFilter !== '' ||
    marketFilter !== ''

  return (
    <div className="orders-container">
      <TabNavigation />
      <div
        className={clsx('orders card noselect', {
          loading: isLoading
        })}
      >
        {walletReady ? (
          <>
            <div className="order-status-filter">{OrderStatusFilterList}</div>
            <div className="order-history-filter-container">
              <div className="order-history-filter">
                <DatePicker
                  dateRange={dateRange}
                  setDateRange={date => setFilter('date', date)}
                />
                {!isLiquidation && (
                  <OrderSideFilter
                    value={orderSideFilter}
                    optionList={ORDER_SIDE_FILTER_LIST}
                    onUpdate={side => setFilter('side', side)}
                  />
                )}
                <MarketFilter
                  value={marketFilter}
                  onUpdate={symbol => setFilter('market', symbol)}
                />
                {hasFilter && (
                  <div
                    className="reset-filter-button-text"
                    onClick={resetFiler}
                  >
                    Clear
                  </div>
                )}
              </div>
            </div>
            <div className="table-list-container">
              <table className="table-list">
                <thead>
                  <tr className="table-list-header noselect">
                    {HistoryHeaderList}
                    {!isLiquidation && (
                      <th className="table-list-cell align-center" />
                    )}
                    {isOpenOrder && orderHistoryNotEmpty && (
                      <th className="table-list-cell align-right">
                        <button
                          className="cancel-order-button"
                          onClick={onCancelAll}
                        >
                          Cancel All
                        </button>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderHistoryNotEmpty ? (
                    OrderHistoryList
                  ) : (
                    <TableListNoDataRow />
                  )}
                </tbody>
              </table>
            </div>
            {totalPage > 0 && (
              <Pagination
                page={page}
                pageCount={totalPage}
                onPageChange={setPage}
              />
            )}
          </>
        ) : (
          <ConnectWalletView />
        )}
      </div>
      <CancelAllConfirmationPopup symbol={marketFilter} />
    </div>
  )
}
